import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { UtilizationBar } from '../UtilizationBar';
import { PersonTaskStatsCards } from '../PersonTaskStats';
import { updatePersonServices } from '../../../lib/peopleApi';
import type { Person, Allocation, PersonTasksResult } from '../../../lib/peopleApi';
import { useServices } from '../../../hooks/useServices';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDepartments } from '../../../hooks/useDepartments';
import { useUpdatePersonContact } from '../../../hooks/usePeople';
import { Popover } from '../../tasks/linear/Popover';
import { Select } from '../../ui/Select';
import { DateField } from '../../ui/DateField';
import { PersonAccountSection } from './PersonAccountSection';

function formatDate(value: string | null): string {
  if (!value) return 'În desfășurare';
  return new Date(value).toLocaleDateString('ro-RO');
}

function AllocationsTable({
  allocations,
  capacity,
}: {
  allocations: Allocation[];
  capacity: number;
}) {
  if (allocations.length === 0) {
    return <p className="text-sm text-gray-500">Nicio alocare pe proiecte.</p>;
  }
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500">
            <th className="py-2 font-medium">Proiect</th>
            <th className="py-2 font-medium">Status</th>
            <th className="py-2 font-medium">Ore/săpt</th>
            <th className="py-2 font-medium">Utilizare</th>
            <th className="py-2 font-medium">Început</th>
            <th className="py-2 font-medium">Sfârșit</th>
          </tr>
        </thead>
        <tbody>
          {allocations.map((a) => (
            <tr key={a.id} className="border-b border-gray-100">
              <td className="py-2 text-gray-900">{a.project.name}</td>
              <td className="whitespace-nowrap py-2 text-gray-600">{a.project.status}</td>
              <td className="whitespace-nowrap py-2 text-gray-600">{a.hours}h</td>
              <td className="whitespace-nowrap py-2 text-gray-600">
                {capacity > 0 ? Math.round((a.hours / capacity) * 100) : 0}%
              </td>
              <td className="whitespace-nowrap py-2 text-gray-600">{formatDate(a.startDate)}</td>
              <td className="whitespace-nowrap py-2 text-gray-600">{formatDate(a.endDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Services nomenclature chips + editor. Mirrors the ServicesSection card on
 * the project overview tab, but persists through PATCH /api/people/:id/services.
 */
function PersonServicesSection({ person }: { person: Person }) {
  const { can } = usePermissions();
  const canEdit = can('people', 'canEdit');
  const { data: services } = useServices();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const attached = person.services ?? [];
  const selectedIds = new Set(attached.map((s) => s.id));

  async function toggleService(serviceId: string) {
    if (pending) return;
    const next = new Set(selectedIds);
    if (next.has(serviceId)) next.delete(serviceId);
    else next.add(serviceId);
    setError(false);
    setPending(true);
    try {
      await updatePersonServices(person.id, [...next]);
      await qc.invalidateQueries({ queryKey: ['person', person.id] });
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="font-semibold text-gray-800">Servicii</h2>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {attached.length === 0 && <p className="text-sm text-gray-500">Niciun serviciu atașat.</p>}
        {attached.map((s) => (
          <span
            key={s.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-700"
          >
            {s.color && (
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            )}
            {s.name}
          </span>
        ))}
        {canEdit && (
          <Popover
            width={260}
            trigger={({ toggle, ref }) => (
              <button
                type="button"
                ref={(el) => ref(el)}
                onClick={toggle}
                disabled={pending}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-50"
              >
                <Pencil size={11} />
                Editează
              </button>
            )}
          >
            {() => (
              <div className="max-h-64 overflow-y-auto py-1">
                {(services ?? []).length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-400">
                    Niciun serviciu definit. Adaugă servicii în Setări.
                  </p>
                )}
                {(services ?? []).map((s) => {
                  const isOn = selectedIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => void toggleService(s.id)}
                      className="ui-menu-item justify-between"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {s.color && (
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: s.color }}
                          />
                        )}
                        <span className="truncate">{s.name}</span>
                      </span>
                      {isOn && <Check size={14} className="shrink-0 text-gray-400" />}
                    </button>
                  );
                })}
              </div>
            )}
          </Popover>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">Actualizarea serviciilor a eșuat.</p>}
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Edit modal for the person profile: full name, email, phone, department
 * (structured select), location, title, capacity, start date and active flag,
 * persisted through PATCH /api/people/:id/contact.
 */
function EditContactModal({ person, onClose }: { person: Person; onClose: () => void }) {
  const update = useUpdatePersonContact(person.id);
  const { data: departments } = useDepartments();

  const [fullName, setFullName] = useState(person.fullName);
  const [email, setEmail] = useState(person.email);
  const [phone, setPhone] = useState(person.phone ?? '');
  const [departmentId, setDepartmentId] = useState(person.departmentId ?? '');
  const [location, setLocation] = useState(person.location ?? '');
  const [title, setTitle] = useState(person.title ?? '');
  const [capacity, setCapacity] = useState(String(person.capacity));
  const [startDate, setStartDate] = useState(person.startDate?.slice(0, 10) ?? '');
  const [isActive, setIsActive] = useState(person.isActive);
  const [error, setError] = useState<string | null>(null);

  const capacityNum = Number(capacity);
  const capacityValid =
    capacity.trim() !== '' &&
    Number.isInteger(capacityNum) &&
    capacityNum >= 0 &&
    capacityNum <= 168;
  const nameValid = fullName.trim().length > 0;
  const emailValid = EMAIL_RE.test(email.trim());
  const isValid = nameValid && emailValid && capacityValid;

  // Câmpul care blochează salvarea, ca să nu rămână butonul gri fără explicație.
  const blockingMessage = !nameValid
    ? 'Completează numele complet.'
    : !emailValid
      ? 'Adresa de email nu este validă.'
      : !capacityValid
        ? 'Capacitatea trebuie să fie un număr întreg între 0 și 168 ore/săptămână.'
        : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || update.isPending) return;
    setError(null);
    update.mutate(
      {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        departmentId: departmentId || null,
        location: location.trim() || null,
        title: title.trim() || null,
        capacity: capacityNum,
        startDate: startDate || null,
        isActive,
      },
      {
        onSuccess: onClose,
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Actualizarea profilului a eșuat'),
      },
    );
  }

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[440px] p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">Editează profilul</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4 px-5 py-4">
            <label className="block text-sm font-medium text-gray-700">
              Nume complet <span className="text-red-500">*</span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="ui-input mt-1"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Email <span className="text-red-500">*</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ui-input mt-1"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Telefon
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+40 7XX XXX XXX"
                className="ui-input mt-1"
              />
            </label>
            <Select
              label="Departament"
              clearable
              value={departmentId}
              onChange={setDepartmentId}
              placeholder="Fără departament"
              options={(departments ?? []).map((d) => ({
                value: d.id,
                label: d.name,
                color: d.color,
              }))}
            />
            <label className="block text-sm font-medium text-gray-700">
              Locație
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="ex., București"
                className="ui-input mt-1"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Titlu
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex., Senior Developer"
                className="ui-input mt-1"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-gray-700">
                Capacitate (ore/săpt.)
                <input
                  type="number"
                  min={0}
                  max={168}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  className="ui-input mt-1"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Data început
                <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full" />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Activ
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {!error && blockingMessage && (
              <p className="text-sm text-amber-600">{blockingMessage}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              Anulează
            </button>
            <button
              type="submit"
              disabled={!isValid || update.isPending}
              className="ui-btn-primary"
            >
              {update.isPending ? 'Se salvează…' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export interface PersonOverviewTabProps {
  person: Person;
  workload?: PersonTasksResult;
  workloadLoading: boolean;
}

/**
 * Overview tab for the Team Member detail page (CLE-237). Mirrors the
 * Project / Client overview tab: contact + capacity cards, task KPI cards and
 * the capacity allocations table. SmartBill costs live in the Invoices tab now.
 */
export function PersonOverviewTab({ person, workload, workloadLoading }: PersonOverviewTabProps) {
  const { can } = usePermissions();
  const canEdit = can('people', 'canEdit');
  const [showContactEdit, setShowContactEdit] = useState(false);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Contact</h2>
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowContactEdit(true)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
              >
                <Pencil size={11} />
                Editează
              </button>
            )}
          </div>
          <dl className="mt-2 space-y-1 text-sm text-gray-600">
            <div className="flex justify-between">
              <dt>Email</dt>
              <dd className="text-gray-900">{person.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Telefon</dt>
              <dd className="text-gray-900">{person.phone ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Departament</dt>
              <dd className="text-gray-900">{person.department ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Locație</dt>
              <dd className="text-gray-900">{person.location ?? '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="font-semibold text-gray-800">Capacitate</h2>
          <p className="mt-2 text-sm text-gray-600">
            {person.allocatedHours} din {person.capacity} ore/săptămână alocate
          </p>
          <div className="mt-3">
            <UtilizationBar percent={person.utilizationPercent} />
          </div>
        </div>
      </div>

      <PersonAccountSection person={person} />

      <PersonServicesSection person={person} />

      {!workloadLoading && workload && <PersonTaskStatsCards stats={workload.stats} />}

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 font-semibold text-gray-800">Alocări de capacitate</h2>
        <AllocationsTable allocations={person.allocations} capacity={person.capacity} />
      </div>

      {showContactEdit && (
        <EditContactModal person={person} onClose={() => setShowContactEdit(false)} />
      )}
    </div>
  );
}
